import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getFactoringDetalleOpe } from '../api/apiConfig';
import { MdReceipt } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import * as XLSX from 'xlsx';

const DetalleOtorgamientos = () => {
  const { user: userData } = useUser();
  const [factoringData, setFactoringData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fechaInforme, setFechaInforme] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 30;

  useEffect(() => {
    if (userData) {
      fetchFactoringData();
    }
  }, [userData]);

  const fetchFactoringData = async () => {
    try {
      setLoading(true);
      const data = await getFactoringDetalleOpe(userData);
      
      if (Array.isArray(data) && data.length > 0) {
        setFactoringData(data);
        setFechaInforme(data[0].FECHA_INFORME);
      } else {
        setError('No hay datos disponibles');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(3).replace('.', ',')}%`;
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-CL');
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.FEC_OPE, item.NUM_OPE, item.RUT_CLI, item.NOM_CLI, item.EJECUTIVO, item.MON_ANT, item.DIF_PRE, item.COMISION, item.GASTOS, item.TAS_NEG, item.CF, item.SPREAD, item.MARGEN_NEGOCIO];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(factoringData);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const clearSearch = () => { setSearchTerm(''); setCurrentPage(1); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const exportData = dataToExport.map(item => ({
      'Fecha Operación': formatDate(item.FEC_OPE),
      'N° Operación': item.NUM_OPE,
      'RUT Cliente': item.RUT_CLI,
      'Nombre Cliente': item.NOM_CLI,
      'Ejecutivo': item.EJECUTIVO,
      'Monto Anticipado': item.MON_ANT,
      'Diferencia Precio': item.DIF_PRE,
      'Comisión': item.COMISION,
      'Gastos': item.GASTOS,
      'Tasa Negocio (%)': item.TAS_NEG,
      'CF (%)': item.CF.toString().replace('.', ','),
      'Spread (%)': (item.SPREAD * 100).toFixed(3).replace('.', ','),
      'Margen Negocio': item.MARGEN_NEGOCIO
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle Otorgamientos');
    
    XLSX.writeFile(wb, `detalle_otorgamientos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="panel-container">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Cargando datos...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-container">
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-container">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Detalle de Otorgamientos</h1>
      </div>

      {fechaInforme && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-blue-800">
              <strong>Fecha de actualización de reporte:</strong> {formatDate(fechaInforme)}
            </div>
          </div>
        </div>
      )}

      <div className="seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0', fontSize: '1.2rem', fontWeight: '600'}}>
            Operaciones de Factoring
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({factoringData.length} registros)
            </span>
          </h3>
          {factoringData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {factoringData.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {searchTerm && <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== factoringData.length ? ` de ${factoringData.length} registros` : ''}</span>}</div>}
          </div>
        )}
        
        {sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
        <div className="tabla-container">
          <table className="w-full" style={{fontSize: '0.875rem', tableLayout: 'fixed', width: '100%'}}>
            <thead>
              <tr>
                <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FEC_OPE')}>Fecha Operación {getSortIcon('FEC_OPE')}</th>
                <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NUM_OPE')}>N° Operación {getSortIcon('NUM_OPE')}</th>
                <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT_CLI')}>RUT Cliente {getSortIcon('RUT_CLI')}</th>
                <th style={{width: '250px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NOM_CLI')}>Nombre Cliente {getSortIcon('NOM_CLI')}</th>
                <th style={{width: '180px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('EJECUTIVO')}>Ejecutivo {getSortIcon('EJECUTIVO')}</th>
                <th style={{width: '130px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MON_ANT')}>Monto Anticipado {getSortIcon('MON_ANT')}</th>
                <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('DIF_PRE')}>Diferencia Precio {getSortIcon('DIF_PRE')}</th>
                <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('COMISION')}>Comisión {getSortIcon('COMISION')}</th>
                <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('GASTOS')}>Gastos {getSortIcon('GASTOS')}</th>
                <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('TAS_NEG')}>Tasa Negocio {getSortIcon('TAS_NEG')}</th>
                <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CF')}>CF {getSortIcon('CF')}</th>
                <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('SPREAD')}>Spread {getSortIcon('SPREAD')}</th>
                <th style={{width: '130px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MARGEN_NEGOCIO')}>Margen Negocio {getSortIcon('MARGEN_NEGOCIO')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((item, index) => (
                <tr key={index}>
                  <td>{formatDate(item.FEC_OPE)}</td>
                  <td>{item.NUM_OPE}</td>
                  <td>{item.RUT_CLI}</td>
                  <td>{item.NOM_CLI}</td>
                  <td>{item.EJECUTIVO}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.MON_ANT)}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.DIF_PRE)}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.COMISION)}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.GASTOS)}</td>
                  <td style={{textAlign: 'center'}}>{item.TAS_NEG}%</td>
                  <td style={{textAlign: 'center'}}>{item.CF.toString().replace('.', ',')}%</td>
                  <td style={{textAlign: 'center'}}>{formatPercentage(item.SPREAD)}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.MARGEN_NEGOCIO)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        
        {sortedData.length > itemsPerPage && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalleOtorgamientos;